import React from "react";
import "./EditUser.scss";
import * as coreApi from "../../shared/core-api-client";
import * as _ from "lodash";
import { Button, Dialog, DialogContent, TextField, DialogActions, Select, MenuItem, Chip, DialogTitleWithCloseButton, InlineInfoTooltip, InfoAlert, KeyValueEditor, Checkbox, LoadingContainer, CircularProgress } from "../../shared/components";
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";
import { useSettings } from "../../shared/providers/SettingsProvider";

const schema = z.object({
    sendWelcomeEmail: z.boolean(),
    name: z.string({ required_error: "Please enter a name for the user." }).min(1, { message: "Please enter a name for the user." }),
    emailAddress: z.string({ required_error: "Please enter an email address for the user." }).includes("@", { message: "Please enter a valid email address." }),
    roles: z.array(z.string()),
    groups: z.array(z.string()),
    userMetadata: z.array(z.object({
        field: z.string(),
        value: z.string()
    })),
    explicitTags: z.object({
        allowed: z.array(z.object({
            key: z.string(),
            value: z.string(),
        })),
        denied: z.array(z.object({
            key: z.string(),
            value: z.string(),
        })),
    }),
});
type FormValues = z.infer<typeof schema>;

interface EditUserProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    roles: coreApi.RoleLookup[],
    groups: coreApi.GroupLookup[],
    user?: coreApi.UserListModel,
    showSuccess: typeof showSuccess,
    showError: typeof showError,
    onSuccess: () => void
}

function EditUser(props: EditUserProps) {
    const { open, setOpen, roles, groups, user: _user, showSuccess, showError, onSuccess } = props;
    const defaultValues: FormValues = { sendWelcomeEmail: true, roles: [], groups: [], userMetadata: [], explicitTags: { allowed: [], denied: [] } };
    const { handleSubmit, reset, setError, control, watch } = useForm({ resolver: zodResolver(schema), defaultValues });
    const sendWelcomeEmail = watch("sendWelcomeEmail", false);
    const [formErrors, setFormErrors] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [user, setUser] = React.useState<coreApi.UserDisplayModel>(null);
    const settings = useSettings();

    React.useEffect(() => {
        if (_user) {
            new coreApi.UsersClient(settings.coreApiUrl).getById(_user.id)
                .then(result => {
                    setUser(result);
                    reset({ ...defaultValues, sendWelcomeEmail: false, ...result });
                });
        }
        else {
            reset(defaultValues);
        }
    }, [_user]);

    const closeDialog = () => {
        setOpen(false);
    }

    const attachFormErrors = (errorDictionary: object) => {
        _.forOwn(errorDictionary, (value, key) => {
            if (key === "non_field_errors") {
                setFormErrors(value[0]);
            }
            else {
                setError(_.camelCase(key) as keyof FormValues, { type: "server", message: value[0] });
            }
        });
    }

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        setSaving(true);
        const model = {
            domainName: settings.domainName,
            ...user,
            ...formModel,
        };

        // Bulk create...
        if (!user && (formModel.name.includes(",") || formModel.emailAddress.includes(","))) {
            let names = formModel.name.split(",").map(a => a.trim()) as string[];
            let emailAddresses = formModel.emailAddress.split(",").map(a => a.trim()) as string[];

            if (names.length !== emailAddresses.length) {
                setError("name", { type: "validation", message: "Length of names and email addresses do not match.  Please double-check both." });
                setSaving(false);
                return;
            }

            let apiCalls = _.zip(names, emailAddresses).map(async ([name, emailAddress], _index) => {
                return await new coreApi.UsersClient(settings.coreApiUrl)
                    .create(
                        formModel.sendWelcomeEmail,
                        {
                            domainName: settings.domainName,
                            ...formModel,
                            name,
                            emailAddress,
                        }
                    )
                    .then(() => showSuccess(`${name} has been created successfully.`))
                    .catch((error: coreApi.ApiException) => showError(`${name} was not created successfully - ${error.response}`));
            });

            Promise.all(apiCalls).then(() => {
                closeDialog();
                onSuccess();
                setSaving(false);
            });

            return;
        }

        const apiCall = user ?
            new coreApi.UsersClient(settings.coreApiUrl).update(user.id, model) :
            new coreApi.UsersClient(settings.coreApiUrl).create(formModel.sendWelcomeEmail, model);

        (apiCall as Promise<any>)
            .then(() => {
                closeDialog();
                showSuccess(`User has been ${user ? "updated" : "created"} successfully.`);
                onSuccess();
            })
            .catch((ex: coreApi.ApiException) => {
                if (ex.status === 400) {
                    attachFormErrors((ex.result as coreApi.ValidationErrorResponse).errors ?? {});
                }
                else {
                    showError(ex.response);
                }
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const getRole = (id) => {
        return roles.find(r => r.id === id);
    }

    const getGroup = (id) => {
        return groups.find(g => g.id === id);
    }

    return (
        <Dialog
            id="edit-user-dialog"
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeDialog();
                }
            }}
            aria-labelledby="form-dialog-title">
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                    {`${user ? "Edit " + user.name : "Create User"}`}
                </DialogTitleWithCloseButton>
                <DialogContent className="vertical-form">
                    {
                        sendWelcomeEmail &&
                        <InfoAlert className="margin-bottom-2">
                            A welcome email will be sent to this user, containing instructions for login/registration.
                        </InfoAlert>
                    }
                    {
                        !user &&
                        <>
                            <div className="vertical-form-field">Send Welcome Email?</div>
                            <Controller
                                name="sendWelcomeEmail"
                                control={control}
                                defaultValue={true}
                                render={({
                                    field: { onChange, value },
                                }) => (
                                    <Checkbox
                                        className="align-self-start"
                                        checked={value}
                                        onChange={(event) => onChange(event.target.checked)}
                                        color="primary"
                                    />
                                )}
                            />
                        </>
                    }

                    <div className="vertical-form-field">Name</div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                                autoFocus
                            />
                        )}
                    />

                    <div className="vertical-form-field">Email Address</div>
                    <Controller
                        name="emailAddress"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                                disabled={user != null}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Roles</div>
                    <Controller
                        name="roles"
                        control={control}
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <Select
                                multiple
                                error={!!error}
                                value={value}
                                onChange={(event) => onChange(event.target.value as string[])}
                                variant="outlined"
                                fullWidth
                                inputProps={{
                                    id: 'roles',
                                }}
                                renderValue={(selected) => (
                                    <div>
                                        {(selected as string[] ?? []).map((value) => (
                                            <Chip key={value} label={getRole(value)?.name} color="primary" className="multiselect-chip" />
                                        ))}
                                    </div>
                                )}
                            >
                                {
                                    roles
                                        .filter(role => role.isAssignable)
                                        .map((role) => (
                                            <MenuItem key={role.id} value={role.id}>
                                                {role.name}
                                            </MenuItem>
                                        ))
                                }
                            </Select>
                        )}
                    />

                    <div className="vertical-form-field">Groups</div>
                    <Controller
                        name="groups"
                        control={control}
                        defaultValue={[]}
                        render={({
                            field: { value, onChange },
                        }) => (
                            <Select
                                className="margin-bottom-2"
                                multiple
                                value={value}
                                onChange={onChange}
                                variant="outlined"
                                fullWidth
                                inputProps={{
                                    id: 'groups',
                                }}
                                renderValue={(selected) => (
                                    <div>
                                        {(selected as string[] ?? []).map((value) => (
                                            <Chip key={value} label={getGroup(value)?.name} color="primary" className="multiselect-chip" />
                                        ))}
                                    </div>
                                )}
                            >
                                {
                                    groups
                                        .map((group) => (
                                            <MenuItem key={group.id} value={group.id}>
                                                {group.name}
                                            </MenuItem>
                                        ))
                                }
                            </Select>
                        )}
                    />

                    <div className="vertical-form-field">Metadata
                        <InlineInfoTooltip
                            text="Any additional information about the user can be captured here.  This will be available to the application and can be utilized for custom logic/bindings in storylines."
                        />
                    </div>
                    <Controller
                        name="userMetadata"
                        control={control}
                        render={({
                            field: { value, onChange },
                        }) => (
                            <KeyValueEditor
                                keyName="field"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Allowed Tags
                        <InlineInfoTooltip
                            text="Any additional permission tags can be added here.  This will be available to the application and can be utilized for access control in storylines."
                        />
                    </div>
                    <Controller
                        name="explicitTags.allowed"
                        control={control}
                        render={({
                            field: { onChange, value },
                        }) => (
                            <KeyValueEditor
                                keyName="key"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Denied Tags
                        <InlineInfoTooltip
                            text="Any additional permission tags can be added here.  This will be available to the application and can be utilized for access control in storylines."
                        />
                    </div>
                    <Controller
                        name="explicitTags.denied"
                        control={control}
                        render={({
                            field: { onChange, value },
                        }) => (
                            <KeyValueEditor
                                keyName="key"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />

                    {
                        formErrors &&
                        <div className="form-errors">
                            {formErrors}
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained" color="primary" disabled={saving} startIcon={ saving && <CircularProgress size="small" color="success" style={{ width: 16, height: 16 }} />}>
                        {`${user?.id ? "Update" : "Create"} User`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default connect(
    null,
    { showSuccess, showError })(EditUser);